import apiClient from './client';
import type { AppConfig, ModelConfig, ModelsResponse, PromptVersion, RagConfig } from '@/types/config';

export type ConfigSide = 'left' | 'right';

export interface StageConditions {
  max_turns?: number | '';
  force_advance_on_max_turns?: boolean;
  intent?: string;
  keywords?: string[];
  relation?: 'or' | 'and';
}

export type StageControlLevel = 'force' | 'strict' | 'normal' | 'suggested' | 'flexible';

export interface StageConfig {
  id: string;
  name: string;
  enabled: boolean;
  focus?: string;
  prompt: string;
  conditions: StageConditions;
  example_questions?: string[];
  children?: StageConfig[];
}

export interface StageControlConfig {
  control_level: StageControlLevel;
  stages: StageConfig[];
}

export interface StageConfigVersion {
  version: string;
  name: string;
  stages?: StageControlConfig | Record<string, StageConfig>;
  stage_control?: StageControlConfig;
  hash?: string;
  created_at?: string | null;
  created_by?: string;
  active?: boolean;
}

/**
 * 获取配置项。
 * @param key 配置键名
 */
export async function getConfig<T = unknown>(key: string): Promise<T> {
  const response = await apiClient.get<T>(`/config/${key}`);
  return response.data as unknown as T;
}

/**
 * 更新配置项。
 * @param key 配置键名
 * @param value 配置值
 */
export async function updateConfig<T = unknown>(key: string, value: T): Promise<T> {
  const response = await apiClient.put<T>(`/config/${key}`, { value });
  return response.data as unknown as T;
}

/**
 * 获取可用模型列表。
 */
export async function getAvailableModels(): Promise<string[]> {
  const response = await apiClient.get<ModelsResponse>('/config/models');
  const data = response.data as unknown as ModelsResponse;
  return data.models || [];
}

/**
 * 获取完整应用配置。
 */
export async function getAppConfig(): Promise<AppConfig> {
  const response = await apiClient.get<AppConfig>('/config');
  return response.data as unknown as AppConfig;
}

/**
 * 更新模型配置。
 * @param side 'left' | 'right'
 * @param config 模型配置对象
 */
export async function updateModelConfig(side: 'left' | 'right', config: ModelConfig): Promise<ModelConfig> {
  const response = await apiClient.put<ModelConfig>(`/config/model/${side}`, config);
  return response.data as unknown as ModelConfig;
}

export async function getPromptVersions(side?: ConfigSide): Promise<PromptVersion[]> {
  const path = side ? `/config/prompt/${side}/versions` : '/config/prompt/versions';
  const response = await apiClient.get<PromptVersion[]>(path);
  return response.data as unknown as PromptVersion[];
}

export async function createPromptVersion(text: string, name?: string, side?: ConfigSide): Promise<PromptVersion> {
  const path = side ? `/config/prompt/${side}/versions` : '/config/prompt/versions';
  const response = await apiClient.post<PromptVersion>(path, { text, name });
  return response.data as unknown as PromptVersion;
}

export async function activatePromptVersion(version: string, side?: ConfigSide): Promise<PromptVersion> {
  const path = side ? `/config/prompt/${side}/versions/${version}/activate` : `/config/prompt/versions/${version}/activate`;
  const response = await apiClient.put<PromptVersion>(path, {});
  return response.data as unknown as PromptVersion;
}

export async function getStageConfigs(side?: ConfigSide): Promise<StageControlConfig> {
  const path = side ? `/config/stages/side/${side}` : '/config/stages';
  const response = await apiClient.get<StageControlConfig>(path);
  return response.data as unknown as StageControlConfig;
}

export async function updateStageConfigs(value: StageControlConfig, side?: ConfigSide): Promise<StageControlConfig> {
  const path = side ? `/config/stages/side/${side}` : '/config/stages';
  const response = await apiClient.put<StageControlConfig>(path, { value });
  return response.data as unknown as StageControlConfig;
}

export async function getStageConfigVersions(side?: ConfigSide): Promise<StageConfigVersion[]> {
  const path = side ? `/config/stages/side/${side}/versions` : '/config/stages/versions';
  const response = await apiClient.get<StageConfigVersion[]>(path);
  return response.data as unknown as StageConfigVersion[];
}

export async function createStageConfigVersion(stages: StageControlConfig, name?: string, side?: ConfigSide): Promise<StageConfigVersion> {
  const path = side ? `/config/stages/side/${side}/versions` : '/config/stages/versions';
  const response = await apiClient.post<StageConfigVersion>(path, { stages, name });
  return response.data as unknown as StageConfigVersion;
}

export async function activateStageConfigVersion(version: string, side?: ConfigSide): Promise<StageConfigVersion> {
  const path = side ? `/config/stages/side/${side}/versions/${version}/activate` : `/config/stages/versions/${version}/activate`;
  const response = await apiClient.put<StageConfigVersion>(path, {});
  return response.data as unknown as StageConfigVersion;
}

export async function getRagConfig(side: ConfigSide): Promise<RagConfig> {
  const response = await apiClient.get<RagConfig>(`/config/rag/${side}`);
  return response.data as unknown as RagConfig;
}

export async function updateRagConfig(side: ConfigSide, value: RagConfig): Promise<RagConfig> {
  const response = await apiClient.put<RagConfig>(`/config/rag/${side}`, { value });
  return response.data as unknown as RagConfig;
}
